import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Optional label shown in the fallback (e.g. element type) */
  label?: string;
}

interface State {
  hasError: boolean;
  message: string;
}

/**
 * Wraps a single slide element so a crash in one element doesn't blank
 * the entire slide or lesson. Renders a subtle inline warning instead.
 */
export class ElementErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    const message = error instanceof Error ? error.message : String(error);
    return { hasError: true, message };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string }) {
    console.error(
      `[Lesson Player] Element render error${this.props.label ? ` (${this.props.label})` : ""}:`,
      error,
      info?.componentStack
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700"
          role="alert"
        >
          <span aria-hidden>⚠️</span>
          <span>
            {this.props.label
              ? `Could not display ${this.props.label}.`
              : "This element could not be displayed."}
          </span>
        </div>
      );
    }
    return this.props.children;
  }
}
